import * as React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthorization } from '../hooks/use-authorization';
import { useDomains } from '../state/domain-state';
import { useSubdomains, type CreateSubdomainInput } from '../state/subdomain-state';
import { useBreadcrumb } from '../state/breadcrumb-state';
import { useToast } from '../state/toast-state';
import * as api from '../services/api-client';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { EmptyState } from '../components/ui/empty-state';
import { ErrorState } from '../components/ui/error-state';
import { InlineForm, type InlineFormField } from '../components/ui/inline-form';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ResolvedUserName } from '../components/users/ResolvedUserName';
import type { Assignment } from '../state/assignment-state';

/**
 * DomainDetailPage — Displays domain metadata and its subdomain list.
 * System_Admin can edit credential assignment.
 * Subdomain rows are clickable, navigating to the subdomain detail view.
 *
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 5.1, 10.2, 10.4
 */

export function DomainDetailPage(): React.JSX.Element {
  const { domainId } = useParams<{ domainId: string }>();
  const navigate = useNavigate();
  const { isSystemAdmin } = useAuthorization();
  const { state: domainState, loadDomains } = useDomains();
  const { state: subdomainState, loadSubdomains, createSubdomain } = useSubdomains();
  const { setSegments } = useBreadcrumb();
  const { showToast } = useToast();
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [showEditForm, setShowEditForm] = React.useState(false);
  const [assignmentCounts, setAssignmentCounts] = React.useState<Record<string, number>>({});

  // Load domains if not already loaded
  React.useEffect(() => {
    if (domainState.domains.length === 0 && !domainState.isLoading) {
      loadDomains();
    }
  }, [domainState.domains.length, domainState.isLoading, loadDomains]);

  // Find domain by ID
  const domain = React.useMemo(
    () => domainState.domains.find((d) => d.domainId === domainId) ?? null,
    [domainState.domains, domainId],
  );

  // Set breadcrumb segments
  React.useEffect(() => {
    if (domain) {
      setSegments([
        { label: 'Domains', href: '/admin-console/domains' },
        { label: domain.domainName },
      ]);
    }
    return () => {
      setSegments([]);
    };
  }, [domain, setSegments]);

  // Load subdomains when domainId is available
  React.useEffect(() => {
    if (domainId) {
      loadSubdomains(domainId);
    }
  }, [domainId, loadSubdomains]);

  // Track assignment counts per subdomain using direct API calls
  React.useEffect(() => {
    if (subdomainState.isLoading || subdomainState.subdomains.length === 0) return;

    let cancelled = false;
    const fetchCounts = async (): Promise<void> => {
      const counts: Record<string, number> = {};
      await Promise.all(
        subdomainState.subdomains.map(async (sub) => {
          try {
            const resp = await api.get<{ assignments?: readonly Assignment[] }>(
              `/admin/subdomains/${sub.subdomainId}/assignments`,
            );
            counts[sub.subdomainId] = resp.assignments?.length ?? 0;
          } catch {
            counts[sub.subdomainId] = 0;
          }
        }),
      );
      if (!cancelled) {
        setAssignmentCounts(counts);
      }
    };
    fetchCounts();
    return () => { cancelled = true; };
  }, [subdomainState.subdomains, subdomainState.isLoading]);

  const handleSubdomainClick = React.useCallback(
    (subdomainId: string): void => {
      navigate(`/admin-console/domains/${domainId}/subdomains/${subdomainId}`);
    },
    [navigate, domainId],
  );

  const handleSubdomainKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>, subdomainId: string): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        navigate(`/admin-console/domains/${domainId}/subdomains/${subdomainId}`);
      }
    },
    [navigate, domainId],
  );

  const subdomainFormFields: readonly InlineFormField[] = React.useMemo(() => [
    { name: 'subdomain', label: 'Subdomain Label', placeholder: 'e.g. park, api, www', required: true },
  ], []);

  const handleAddSubdomainSubmit = React.useCallback(
    async (data: Record<string, string>): Promise<void> => {
      if (!domainId) return;
      const input: CreateSubdomainInput = {
        domainId,
        subdomain: data['subdomain'] ?? '',
      };
      await createSubdomain(input);
      setShowAddForm(false);
      showToast(`Subdomain "${input.subdomain}" created successfully.`, 'success');
    },
    [domainId, createSubdomain, showToast],
  );

  const handleAddSubdomainCancel = React.useCallback((): void => {
    setShowAddForm(false);
  }, []);

  const handleShowAddForm = React.useCallback((): void => {
    setShowAddForm(true);
  }, []);

  // ─── Edit Domain Handlers ──────────────────────────────────────────────────

  const editDomainFields: readonly InlineFormField[] = React.useMemo(() => [
    { name: 'credentialId', label: 'Credential ID', placeholder: domain?.credentialId ?? '', required: false },
    { name: 'cloudflareZoneId', label: 'Cloudflare Zone ID', placeholder: domain?.cloudflareZoneId ?? '', required: false },
  ], [domain]);

  const handleEditDomainSubmit = React.useCallback(
    async (data: Record<string, string>): Promise<void> => {
      if (!domainId) return;
      const updates: Record<string, string> = {};
      if (data['credentialId']?.trim()) updates['credentialId'] = data['credentialId'].trim();
      if (data['cloudflareZoneId']?.trim()) updates['cloudflareZoneId'] = data['cloudflareZoneId'].trim();

      if (Object.keys(updates).length === 0) {
        showToast('No changes provided.', 'info');
        setShowEditForm(false);
        return;
      }

      await api.put(`/admin/domains/${domainId}`, updates);
      setShowEditForm(false);
      showToast('Domain updated successfully.', 'success');
      // Reload domains to reflect changes
      await loadDomains();
    },
    [domainId, showToast, loadDomains],
  );

  const handleEditDomainCancel = React.useCallback((): void => {
    setShowEditForm(false);
  }, []);

  const handleShowEditForm = React.useCallback((): void => {
    setShowEditForm(true);
  }, []);

  const handleRetrySubdomains = React.useCallback((): void => {
    if (domainId) {
      loadSubdomains(domainId);
    }
  }, [domainId, loadSubdomains]);

  // Loading state: domains still loading
  if (domainState.isLoading) {
    return <Spinner label="Loading domain..." />;
  }

  // Error state: failed to load domains
  if (domainState.error) {
    return <ErrorState message={domainState.error} onRetry={loadDomains} />;
  }

  // 404 state: domain not found
  if (!domain) {
    return (
      <section aria-labelledby="domain-not-found-heading" className="flex flex-col items-center gap-4 py-12">
        <h2 id="domain-not-found-heading" className="text-xl font-semibold text-foreground">
          Domain Not Found
        </h2>
        <p className="text-sm text-muted-foreground">
          The domain you are looking for does not exist or has been removed.
        </p>
        <Link
          to="/admin-console/domains"
          className="text-sm font-medium text-primary underline hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          ← Back to Domains
        </Link>
      </section>
    );
  }

  const formattedCreatedDate = new Date(domain.createdAt).toLocaleDateString();

  return (
    <section aria-labelledby="domain-detail-heading">
      {/* Domain Metadata Header */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="domain-detail-heading" className="text-2xl font-semibold text-foreground">
                {domain.domainName}
              </h2>
              <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Zone ID:</dt>
                  <dd className="font-mono text-xs">{domain.cloudflareZoneId}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Credential ID:</dt>
                  <dd className="font-mono text-xs">{domain.credentialId}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Created:</dt>
                  <dd>{formattedCreatedDate}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Created by:</dt>
                  <dd><ResolvedUserName userId={domain.createdBy} /></dd>
                </div>
              </dl>
            </div>
            {isSystemAdmin && (
              <Button variant="outline" size="sm" onClick={handleShowEditForm} disabled={showEditForm}>
                Edit
              </Button>
            )}
          </div>
          {isSystemAdmin && showEditForm && (
            <div className="mt-4 border-t pt-4">
              <InlineForm
                fields={editDomainFields}
                onSubmit={handleEditDomainSubmit}
                onCancel={handleEditDomainCancel}
                submitLabel="Update Domain"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Leave a field empty to keep its current value.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subdomains Section */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Subdomains</h3>
        <Button onClick={handleShowAddForm} disabled={showAddForm} size="sm">
          Add Subdomain
        </Button>
      </div>

      {/* Inline Add Subdomain Form */}
      {showAddForm && (
        <div className="mb-4">
          <InlineForm
            fields={subdomainFormFields}
            onSubmit={handleAddSubdomainSubmit}
            onCancel={handleAddSubdomainCancel}
            submitLabel="Create Subdomain"
          />
          {domain && (
            <p className="mt-2 text-xs text-muted-foreground">
              FQDN preview: <span className="font-mono">{'<label>'}. {domain.domainName}</span>
            </p>
          )}
        </div>
      )}

      {/* Subdomain List */}
      {subdomainState.isLoading ? (
        <Spinner label="Loading subdomains..." />
      ) : subdomainState.error ? (
        <ErrorState message={subdomainState.error} onRetry={handleRetrySubdomains} />
      ) : subdomainState.subdomains.length === 0 ? (
        <EmptyState
          title="No subdomains"
          description="This domain does not have any subdomains yet. Create the first subdomain to get started."
          actionLabel="Add Subdomain"
          onAction={handleShowAddForm}
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Label</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">FQDN</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Created</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Assignments</th>
              </tr>
            </thead>
            <tbody>
              {subdomainState.subdomains.map((subdomain) => (
                <tr
                  key={subdomain.subdomainId}
                  role="button"
                  tabIndex={0}
                  aria-label={`Subdomain ${subdomain.subdomainLabel}`}
                  onClick={() => handleSubdomainClick(subdomain.subdomainId)}
                  onKeyDown={(e) => handleSubdomainKeyDown(e, subdomain.subdomainId)}
                  className="cursor-pointer border-b transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <td className="px-4 py-3 font-medium">{subdomain.subdomainLabel}</td>
                  <td className="px-4 py-3 font-mono text-xs">{subdomain.fqdn}</td>
                  <td className="px-4 py-3">{new Date(subdomain.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary">
                      {assignmentCounts[subdomain.subdomainId] ?? 0}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DNS Records Section */}
      {isSystemAdmin && <DnsRecordsSection domainId={domainId!} />}
    </section>
  );
}

// ─── DNS Records Section ─────────────────────────────────────────────────────

interface DnsRecord {
  readonly id: string;
  readonly type: string;
  readonly name: string;
  readonly content: string;
  readonly ttl: number;
  readonly proxied: boolean;
  readonly managed: boolean;
}

function DnsRecordsSection({ domainId }: { domainId: string }): React.JSX.Element {
  const [records, setRecords] = React.useState<readonly DnsRecord[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchRecords = React.useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<{ records: DnsRecord[] }>(`/admin/domains/${domainId}/dns-records`);
      setRecords(data.records);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load DNS records');
    } finally {
      setIsLoading(false);
    }
  }, [domainId]);

  React.useEffect(() => { fetchRecords(); }, [fetchRecords]);

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">DNS Records (Cloudflare)</h3>
        <Badge variant="outline">{records.filter(r => r.managed).length} managed / {records.filter(r => !r.managed).length} unmanaged</Badge>
      </div>

      {isLoading ? (
        <Spinner label="Loading DNS records from Cloudflare..." />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchRecords} />
      ) : records.length === 0 ? (
        <EmptyState
          title="No DNS records"
          description="No DNS records found in the Cloudflare zone for this domain."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Content</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">TTL</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Proxied</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id} className="border-b">
                  <td className="px-4 py-3">
                    <Badge variant={record.managed ? 'success' : 'warning'}>
                      {record.managed ? 'Managed' : 'Unmanaged'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{record.type}</td>
                  <td className="px-4 py-3 font-mono text-xs">{record.name}</td>
                  <td className="px-4 py-3 font-mono text-xs max-w-[200px] truncate">{record.content}</td>
                  <td className="px-4 py-3">{record.ttl === 1 ? 'Auto' : `${record.ttl}s`}</td>
                  <td className="px-4 py-3">{record.proxied ? '☁️' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
